import { DefaultLoader } from '@euler/app/components/loaders/DefaultLoader';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { ModalWrapper } from '@euler/components/ModalWrapper';
import { FontFamily } from '@euler/components/typography';
import { transparentModalOptions } from '@euler/functions/navigationOptionsBuilder';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useGeneralBottomSheetModal } from '@euler/functions/useBottomSheetModal';
import { useStoreConfiguration } from '@euler/functions/useStoreConfiguration';
import { useAlert } from '@euler/functions/ux/useAlert';
import { usePrompt } from '@euler/functions/ux/usePrompt';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { formatDateTime } from '@euler/utils/formatters';
import { useActionSheet } from '@expo/react-native-action-sheet';
import {
  BottomSheetFooter,
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetTextInput,
} from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import {
  forwardRef,
  memo,
  Suspense,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const TaskTimeoutInfoPage = wrapNavigatorScreen(
  ({
    estimatedFinishTime,
    ...props
  }: {
    estimatedFinishTime: string | Date;
    onContinue?: (reason: string, remarks?: string) => void;
  }) => {
    const isMobile = useIsMobileLayout();
    const navigation = useNavigation();

    const onClose = useCallback(() => {
      navigation.goBack();
    }, [navigation]);

    const onContinue = useCallback(
      (reason: string, remarks?: string) => {
        props.onContinue?.(reason, remarks);
      },
      [props],
    );

    if (isMobile) {
      return (
        <BottomSheetContent
          estimatedFinishTime={estimatedFinishTime}
          onClose={onClose}
          onContinue={onContinue}
        />
      );
    }

    return (
      <ModalContent
        estimatedFinishTime={estimatedFinishTime}
        onClose={onClose}
        onContinue={onContinue}
      />
    );
  },
  transparentModalOptions(),
);

const BottomSheetContent = memo(
  ({
    onClose,
    onContinue,
    ...props
  }: {
    onClose: () => void;
    onContinue: (reason: string, remarks?: string) => void;
    estimatedFinishTime: string | Date;
  }) => {
    const ref = useRef<Ref>(null);
    const [show, setShow] = useState(true);
    const insets = useSafeAreaInsets();
    const continueParams = useRef<{ reason: string; remarks?: string }>();

    const onCancel = useCallback(() => {
      setShow(false);
    }, []);

    const onContinuePress = useCallback(() => {
      const values = ref.current?.getValues();
      if (values == null) return;
      setShow(false);
      continueParams.current = values;
    }, []);

    const { bottomSheetModalRef, ...bottomSheetProps } =
      useGeneralBottomSheetModal({
        show,
        minSnapPoint: '30%',
        mediumSnapPoint: '50%',
        dismissWhenPressBackdrop: false,
        enablePanDownToClose: false,
        onDismiss: () => {
          if (continueParams.current) {
            onClose();
            const { reason, remarks } = continueParams.current;
            setTimeout(() => {
              onContinue(reason, remarks);
            }, 10);
          } else {
            onClose();
          }
        },
      });

    const ScrollViewComponent =
      Platform.OS === 'web' ? ScrollView : BottomSheetScrollView;

    return (
      <BottomSheetModal
        ref={bottomSheetModalRef}
        keyboardBehavior="extend"
        // eslint-disable-next-line @typescript-eslint/no-shadow
        footerComponent={(props: any) => (
          <BottomSheetFooter {...props}>
            <Footer
              onCancel={onCancel}
              onContinue={onContinuePress}
              style={{
                paddingBottom: insets.bottom,
              }}
            />
          </BottomSheetFooter>
        )}
        {...bottomSheetProps}
      >
        <Suspense
          fallback={
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
                alignItems: 'center',
              }}
            >
              <DefaultLoader />
            </ScrollView>
          }
        >
          <ScrollViewComponent keyboardShouldPersistTaps="handled">
            <Content ref={ref} isBottomSheet {...props} />
          </ScrollViewComponent>
        </Suspense>
      </BottomSheetModal>
    );
  },
);

const ModalContent = memo(
  ({
    onClose,
    onContinue,
    ...props
  }: {
    onClose: () => void;
    onContinue: (reason: string, remarks?: string) => void;
    estimatedFinishTime: string | Date;
  }) => {
    const ref = useRef<Ref>(null);
    const { rpx } = useResponsiveContext();

    const onCancel = useCallback(() => {
      onClose();
    }, [onClose]);

    const onContinuePress = useCallback(() => {
      const values = ref.current?.getValues();
      if (values == null) return;
      onClose();
      setTimeout(() => {
        onContinue(values.reason, values.remarks);
      }, 350);
    }, [onClose, onContinue]);

    return (
      <ModalWrapper
        dismissible={false}
        height={Platform.OS === 'web' ? 440 : 480}
      >
        <Suspense
          fallback={
            <ScrollView
              contentContainerStyle={{
                flexGrow: 1,
                alignItems: 'center',
              }}
            >
              <DefaultLoader />
            </ScrollView>
          }
        >
          <ScrollView
            contentContainerStyle={{
              paddingTop: rpx(20),
              paddingBottom: rpx(20),
            }}
          >
            <Content
              ref={ref}
              {...props}
              style={{ paddingTop: 0, paddingBottom: 0 }}
            />
          </ScrollView>
          <Footer
            onCancel={onCancel}
            onContinue={onContinuePress}
            style={{
              paddingTop: rpx(10),
              paddingBottom: rpx(20),
            }}
          />
        </Suspense>
      </ModalWrapper>
    );
  },
);

type Ref = {
  getValues: () => { reason: string; remarks?: string } | undefined;
};

const Content = memo(
  forwardRef<
    Ref,
    {
      estimatedFinishTime: string | Date;
      style?: StyleProp<ViewStyle>;
      onKeywordInputFocus?: () => void;
      isBottomSheet?: boolean;
    }
  >(
    (
      { estimatedFinishTime, style, isBottomSheet, onKeywordInputFocus },
      ref,
    ) => {
      const storeConfiguration = useStoreConfiguration();
      const prompt = usePrompt();
      const alert = useAlert();
      const { rpx } = useResponsiveContext();
      const { showActionSheetWithOptions } = useActionSheet();
      const [reason, setReason] = useState<string>();
      const [remarks, setRemarks] = useState<string>();

      const TextInputComponent = isBottomSheet
        ? BottomSheetTextInput
        : TextInput;

      useImperativeHandle(ref, () => ({
        getValues: () => {
          if (!reason) {
            void alert({ title: '请选择或输入超时原因!' });
            return;
          }
          return { reason, remarks };
        },
      }));

      const onReasonPress = useCallback(() => {
        const reasons = storeConfiguration.predefinedOrderTimeoutReasons?.length
          ? [...storeConfiguration.predefinedOrderTimeoutReasons]
          : ['未知原因'];
        reasons.push('手动输入...');
        showActionSheetWithOptions(
          {
            title: '选择或输入超时原因',
            options: [...reasons, '取消'],
            cancelButtonIndex: reasons.length,
          },
          async buttonIndex => {
            if (buttonIndex == null || buttonIndex === reasons.length) {
              return;
            }
            if (buttonIndex === reasons.length - 1) {
              const input = await prompt({
                title: '请输入超时原因',
                defaultValue: '',
              });
              if (input?.trim()) {
                setReason(input.trim());
              }
            } else {
              setReason(reasons[buttonIndex]);
            }
          },
        );
      }, [
        prompt,
        showActionSheetWithOptions,
        storeConfiguration.predefinedOrderTimeoutReasons,
      ]);

      return (
        <View
          style={style}
          css={`
            padding: ${rpx(20)}px;
          `}
        >
          <Typography.Title level={1} align="center">
            超时工单
          </Typography.Title>
          <Typography.Body mt={rpx(15)} mb={rpx(15)} color="#777">
            为了更好地记录工单进展并提升服务质量，如工单超出预计完成时间，请您提供超时原因及相关备注，感谢您的支持与配合！
          </Typography.Body>
          <StackLayout
            gap={rpx(10)}
            divider={
              <View
                css={`
                  background-color: #ddd;
                  height: ${StyleSheet.hairlineWidth}px;
                `}
              />
            }
          >
            <StackLayout
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography.Label size={rpx(15)} medium>
                预计完工:{' '}
              </Typography.Label>
              <Typography.Label size={rpx(15)} color="#333">
                {formatDateTime(estimatedFinishTime)}
              </Typography.Label>
            </StackLayout>
            <StackLayout
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography.Label size={rpx(15)} medium>
                超时原因:{' '}
              </Typography.Label>
              <TouchableOpacity
                onPress={onReasonPress}
                css={`
                  flex-direction: row;
                  justify-content: flex-end;
                  align-items: center;
                `}
              >
                <Typography.Label
                  size={rpx(15)}
                  color={reason ? '#333' : '#777'}
                >
                  {reason ?? '请选择'}
                </Typography.Label>
                <Icon
                  name="chevron-right"
                  size={rpx(20)}
                  color={reason ? '#333' : '#777'}
                />
              </TouchableOpacity>
            </StackLayout>
            <StackLayout gap={rpx(5)}>
              <Typography.Label size={rpx(15)} medium>
                其他备注:{' '}
              </Typography.Label>
              <TextInputComponent
                textAlignVertical="top"
                multiline
                placeholder="备注"
                placeholderTextColor={'#aaa'}
                clearButtonMode="always"
                value={remarks ?? ''}
                onChangeText={setRemarks}
                onFocus={onKeywordInputFocus}
                style={{
                  paddingHorizontal: rpx(10),
                  paddingVertical: rpx(8),
                  padding: rpx(8),
                  lineHeight: rpx(22),
                  fontSize: rpx(14),
                  height: rpx(80),
                  borderColor: '#ddd',
                  borderWidth: 1,
                  borderRadius: rpx(10),
                  fontFamily: FontFamily.NotoSans.Regular,
                }}
              />
            </StackLayout>
          </StackLayout>
        </View>
      );
    },
  ),
);

const Footer = memo(
  ({
    style,
    onCancel,
    onContinue,
  }: {
    onCancel: () => void;
    onContinue: () => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <StackLayout
        direction="row"
        justifyContent="center"
        alignItems="center"
        gap={rpx(15)}
        style={style}
        css={`
          padding: 0 ${rpx(20)}px;
        `}
      >
        <Center.Touchable
          onPress={onCancel}
          css={`
            border-width: 1px;
            border-color: rgb(232, 232, 232);
            border-radius: 6px;
            height: ${rpx(44)}px;
            padding: 0 ${rpx(20)}px;
            flex: 1;
          `}
        >
          <Typography.Label>返回</Typography.Label>
        </Center.Touchable>
        <Center.Touchable
          onPress={onContinue}
          css={`
            border-width: 1px;
            border-color: ${StatusColors.Info};
            border-radius: 6px;
            background-color: ${StatusColors.Info};
            height: ${rpx(44)}px;
            padding: 0 ${rpx(20)}px;
            flex: 1;
          `}
        >
          <Typography.Label color="#fff">确认并继续</Typography.Label>
        </Center.Touchable>
      </StackLayout>
    );
  },
);
